<template>
  <div class="adj-einv-status-wrapper">
    <button
      v-if="status"
      :id="popoverId"
      type="button"
      :class="['adj-einv-pill', pillConfig.class, sizeClass]"
      :aria-label="pillConfig.text"
      @click.stop="$emit('click')"
    >
      <i v-if="pillConfig.icon" :class="pillConfig.icon"></i>
      <span>{{ pillConfig.text }}</span>
      <span v-if="hasWarning" class="warning-dot" aria-hidden="true"></span>
    </button>

    <b-popover
      v-if="showPopover"
      :target="popoverId"
      triggers="hover focus"
      placement="left"
      custom-class="adj-einv-status-popover"
    >
      <template #title>HĐ điều chỉnh GTGT</template>

      <div class="popover-detail">
        <div
          v-for="item in referenceItems"
          :key="item.label"
          class="detail-row"
        >
          <span class="detail-label">{{ item.label }}</span>
          <span :class="['detail-value', item.className]">{{ item.value }}</span>
        </div>
      </div>
    </b-popover>
  </div>
</template>

<script>
import { ADJUSTMENT_DRAFT_STATUS } from '@/utils/enum';

const S = ADJUSTMENT_DRAFT_STATUS;

const PILL_BY_DRAFT_STATUS = {
  [S.DRAFT]: { class: 'pill-blue', icon: 'fas fa-file-alt', text: 'Nháp' },
  [S.READY_TO_ISSUE]: { class: 'pill-blue', icon: 'fas fa-check-circle', text: 'Sẵn sàng phát hành' },
  [S.ISSUING]: { class: 'pill-yellow', icon: 'fas fa-spinner fa-spin', text: 'Đang PH' },
  [S.ISSUED]: { class: 'pill-green', icon: 'fas fa-check', text: 'Đã phát hành' },
  [S.WAITING_PROVIDER]: { class: 'pill-yellow', icon: 'fas fa-clock', text: 'Chờ CQT' },
  [S.ISSUE_FAILED]: { class: 'pill-red', icon: 'fas fa-exclamation-triangle', text: 'Lỗi phát hành' },
  [S.RETRYING]: { class: 'pill-yellow', icon: 'fas fa-redo', text: 'Thử lại' },
  [S.FAST_SYNCING]: { class: 'pill-yellow', icon: 'fas fa-sync fa-spin', text: 'Đang ĐB FAST' },
  [S.FAST_SYNCED]: { class: 'pill-green', icon: 'fas fa-cloud-upload-alt', text: 'Đã ĐB FAST' },
  [S.FAST_SYNC_FAILED]: { class: 'pill-orange', icon: 'fas fa-exclamation-triangle', text: 'Lỗi ĐB FAST' },
  [S.DELETED]: { class: 'pill-dark', icon: 'fas fa-trash', text: 'Đã xóa' },
  [S.CANCELLED]: { class: 'pill-dark', icon: 'fas fa-ban', text: 'Đã hủy' },
};

const UNKNOWN_PILL = { class: 'pill-gray', icon: null, text: 'Không xác định' };

export default {
  name: 'AdjustmentEInvoiceStatusBadge',

  props: {
    /** Raw AdjustmentDraft.status (DRAFT, ISSUED, …) */
    status: { type: String, default: null },
    invoiceNo: { type: String, default: null },
    pattern: { type: String, default: null },
    serial: { type: String, default: null },
    issuedAt: { type: String, default: null },
    errorMessage: { type: String, default: null },
    taxStatus: { type: String, default: null },
    draftCode: { type: String, default: null },
    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md'].includes(v),
    },
  },

  emits: ['click'],

  data() {
    return {
      popoverId: `adj-einv-pill-${Math.random().toString(36).slice(2, 9)}`,
    };
  },

  computed: {
    pillConfig() {
      if (!this.status) return UNKNOWN_PILL;
      return PILL_BY_DRAFT_STATUS[this.status] || UNKNOWN_PILL;
    },
    showPopover() {
      const withInvoice = [S.ISSUED, S.FAST_SYNCING, S.FAST_SYNCED, S.FAST_SYNC_FAILED].includes(this.status);
      return withInvoice && this.referenceItems.length > 0;
    },
    referenceItems() {
      const items = [];
      if (this.status) {
        items.push({
          label: 'Trạng thái',
          value: this.pillConfig.text,
          className: 'text-muted',
        });
      }
      if (this.draftCode) {
        items.push({
          label: 'Mã nháp',
          value: this.draftCode,
          className: 'text-muted',
        });
      }
      if (this.invoiceNo) {
        items.push({
          label: 'Số HĐ điều chỉnh',
          value: this.invoiceNo,
          className: 'font-weight-bold',
        });
      }
      if (this.pattern || this.serial) {
        items.push({
          label: 'Mẫu / Ký hiệu',
          value: [this.pattern, this.serial].filter(Boolean).join(' / '),
          className: '',
        });
      }
      if (this.issuedAt) {
        items.push({
          label: 'Ngày phát hành',
          value: this.formatDate(this.issuedAt),
          className: '',
        });
      }
      if (this.errorMessage && (this.status === S.ISSUE_FAILED || this.status === S.FAST_SYNC_FAILED)) {
        items.push({
          label: 'Lỗi',
          value: this.errorMessage,
          className: 'text-danger',
        });
      }
      return items;
    },
    sizeClass() {
      return this.size === 'sm' ? 'adj-einv-pill--sm' : '';
    },
    hasWarning() {
      return (
        [S.ISSUE_FAILED, S.FAST_SYNC_FAILED, S.WAITING_PROVIDER].includes(this.status)
        || this.taxStatus === 'rejected'
      );
    },
  },

  methods: {
    formatDate(isoDate) {
      if (!isoDate) return '';
      return new Date(isoDate).toLocaleDateString('vi-VN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.adj-einv-status-wrapper {
  display: inline-block;
}

.adj-einv-pill {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 4px 10px;
  border: none;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  transition: filter 0.15s ease, transform 0.15s ease;
  position: relative;
  white-space: nowrap;
  line-height: 1.4;

  &:hover {
    filter: brightness(0.93);
    transform: translateY(-1px);
  }

  &:focus-visible {
    outline: 2px solid rgba(59, 130, 246, 0.6);
    outline-offset: 2px;
  }

  i {
    font-size: 10px;
    flex-shrink: 0;
  }
}

.adj-einv-pill--sm {
  font-size: 10px;
  padding: 3px 8px;
  gap: 4px;

  i {
    font-size: 9px;
  }
}

.pill-gray {
  background: #f1f3f5;
  color: #868e96;
}
.pill-blue {
  background: #e7f5ff;
  color: #1c7ed6;
}
.pill-yellow {
  background: #fff9db;
  color: #e67700;
}
.pill-green {
  background: #d3f9d8;
  color: #2f9e44;
}
.pill-red {
  background: #ffe3e3;
  color: #c92a2a;
}
.pill-orange {
  background: #fff4e6;
  color: #d9480f;
}
.pill-purple {
  background: #f3d9fa;
  color: #862e9c;
}
.pill-dark {
  background: #e9ecef;
  color: #495057;
}

.warning-dot {
  position: absolute;
  top: -3px;
  right: -3px;
  width: 8px;
  height: 8px;
  background: #c92a2a;
  border-radius: 50%;
  border: 2px solid #fff;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

:deep(.adj-einv-status-popover) {
  min-width: 230px;
  max-width: 300px;

  .popover-header {
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;
    padding: 8px 14px;
  }

  .popover-body {
    padding: 6px 14px 10px;
  }
}

.popover-detail {
  display: flex;
  flex-direction: column;
}

.detail-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 12px;
  padding: 6px 0;
  font-size: 12px;
  border-bottom: 1px solid #f1f3f5;

  &:last-child {
    border-bottom: none;
  }
}

.detail-label {
  color: #868e96;
  flex-shrink: 0;
  font-size: 11px;
}

.detail-value {
  text-align: right;
  word-break: break-word;
  font-size: 12px;
}
</style>
